import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

/**
 * Stop-workflow definitions (nest-logistic-service, /v1/stop-workflows).
 * Milestone/field keys are SYSTEM-GENERATED server-side from the names
 * (camelCase, symbols stripped: "Foto Depan Kendaraan" → "fotoDepanKendaraan")
 * — payloads carry names only; responses echo the generated keys.
 * A workflow is a per-client door-side form: ordered milestones, each with
 * ordered fields the rider must capture at a stop. Definitions only —
 * execution (snapshots on route stops) ships with the route module later.
 */

export type WorkflowStatus = 'ACTIVE' | 'INACTIVE';
/**
 * SIGNATURE is an IMAGE with a different capture: the rider app draws it on a
 * pad and uploads exactly one PNG. It has no config. Offered in the builder
 * only behind a flag (see WorkflowBuilderModal), because a driver app that
 * predates it cannot capture one.
 */
export type WorkflowFieldType = 'IMAGE' | 'TEXT' | 'NUMBER' | 'SIGNATURE';
/** The stop type a workflow gates — PICKUP or DROP_OFF, never both. */
export type WorkflowStopType = 'PICKUP' | 'DROP_OFF';

/**
 * The purpose within the stop type. Lets a client run more than one ACTIVE
 * DROP_OFF workflow — EXIM needs UNLOAD (bongkar muatan) and RETURN_CONTAINER
 * (balikin kontainer) side by side, each with its own milestones. GENERIC is
 * the default (PICKUP + plain single-drop).
 */
export type WorkflowStopIntent = 'GENERIC' | 'UNLOAD' | 'RETURN_CONTAINER';

/**
 * The delivery DIRECTION a workflow serves — the fourth dimension of the default
 * resolution slot `(client, stopType, intent, exim)`, so a client can hold an
 * Import default and an Export default for the same stop type + intent. GENERAL
 * is the back-compat default every pre-exim row reads.
 */
export type WorkflowExim = 'IMPORT' | 'EXPORT' | 'GENERAL';

export interface WorkflowFieldConfig {
  /** IMAGE: exactly N photos. Mutually exclusive with `multiple`. */
  imageCount?: number;
  /** IMAGE: unbounded photo list (min 1). Mutually exclusive with `imageCount`. */
  multiple?: boolean;
  /** TEXT: max character length. */
  maxLength?: number;
  /** NUMBER bounds. */
  min?: number;
  max?: number;
  // SIGNATURE takes no keys at all — send `{}`; the server 422s any key.
}

export interface WorkflowField {
  id: string;
  key: string;
  label: string;
  type: WorkflowFieldType;
  required: boolean;
  config: WorkflowFieldConfig | null;
}

export interface WorkflowMilestone {
  id: string;
  key: string;
  name: string;
  sequence: number;
  /** May be EMPTY: a fieldless milestone is confirmation-only — the rider
   *  slides to confirm and only a timestamp is recorded. */
  fields: WorkflowField[];
}

/** Frozen `{id,name}` snapshot from core-service, captured at write time. */
export interface WorkflowClient {
  id: number;
  name: string;
}

export interface WorkflowSummary {
  id: string;
  /** NULL = a GLOBAL (client-agnostic) workflow, visible to every client. */
  clientID: number | null;
  /** Null when core-service was unreachable at write time, or a global (which
   *  has no client) — fall back to clientID. */
  client: WorkflowClient | null;
  name: string;
  stopType: WorkflowStopType;
  intent: WorkflowStopIntent;
  /** Delivery direction served (GENERAL unless an Import/Export default). */
  exim: WorkflowExim;
  /** The one ACTIVE workflow per (client, stopType, intent) that a stop
   *  resolves to when no workflow is picked. Exactly one per slot. */
  isDefault: boolean;
  status: WorkflowStatus;
  milestoneCount: number;
  fieldCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowDetail {
  id: string;
  /** NULL = a GLOBAL (client-agnostic) workflow, visible to every client. */
  clientID: number | null;
  client: WorkflowClient | null;
  name: string;
  stopType: WorkflowStopType;
  intent: WorkflowStopIntent;
  /** Delivery direction served (GENERAL unless an Import/Export default). */
  exim: WorkflowExim;
  /** See WorkflowSummary.isDefault. */
  isDefault: boolean;
  status: WorkflowStatus;
  milestones: WorkflowMilestone[];
  createdAt: string;
  updatedAt: string;
}

export interface UpsertWorkflowFieldPayload {
  label: string;
  type: WorkflowFieldType;
  required: boolean;
  config?: WorkflowFieldConfig;
}

export interface UpsertWorkflowMilestonePayload {
  name: string;
  sequence: number;
  fields: UpsertWorkflowFieldPayload[];
}

export interface UpsertWorkflowPayload {
  /**
   * NULL = create a GLOBAL (client-agnostic) workflow, visible to every client.
   * A concrete id scopes it to one client. A client-specific default beats a
   * global default for the same slot at approve time.
   */
  clientID: number | null;
  name: string;
  stopType: WorkflowStopType;
  /** Omit for GENERIC; set UNLOAD/RETURN_CONTAINER for a purpose-specific DROP_OFF. */
  intent?: WorkflowStopIntent;
  /** Delivery direction. Omit → server defaults GENERAL. */
  exim?: WorkflowExim;
  /**
   * REQUIRED — the server never infers it. A slot may hold several ACTIVE
   * workflows, so the caller must say outright whether this one is the
   * resolution target. Sending `false` into an empty slot is a 400
   * (WORKFLOW_DEFAULT_REQUIRED); sending `true` when one exists is a 409
   * (WORKFLOW_DEFAULT_EXISTS) — promote via `setDefault` instead.
   */
  isDefault: boolean;
  status: WorkflowStatus;
  milestones: UpsertWorkflowMilestonePayload[];
}

export interface WorkflowListParams {
  clientID?: number;
  /**
   * PICKER flag (route planner): when true AND clientID is set, the list also
   * returns GLOBAL (clientID null) workflows alongside the client's own. The
   * authoring/management screen omits it so it stays client-scoped (own only).
   */
  includeGlobal?: boolean;
  stopType?: WorkflowStopType;
  intent?: WorkflowStopIntent;
  exim?: WorkflowExim;
  status?: WorkflowStatus;
}

interface Envelope<T> {
  status: string;
  message?: string;
  data: T;
}

export const stopWorkflowsAPI = {
  list: async (params: WorkflowListParams = {}): Promise<WorkflowSummary[]> => {
    try {
      const response = await Axios.get<Envelope<WorkflowSummary[]>>(
        '/v1/stop-workflows',
        { params }
      );
      return response.data.data ?? [];
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  get: async (id: string): Promise<WorkflowDetail> => {
    try {
      const response = await Axios.get<Envelope<WorkflowDetail>>(
        `/v1/stop-workflows/${id}`
      );
      return response.data.data;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  create: async (payload: UpsertWorkflowPayload): Promise<WorkflowDetail> => {
    try {
      const response = await Axios.post<Envelope<WorkflowDetail>>(
        '/v1/stop-workflows',
        payload
      );
      return response.data.data;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  update: async (
    id: string,
    payload: UpsertWorkflowPayload
  ): Promise<WorkflowDetail> => {
    try {
      const response = await Axios.put<Envelope<WorkflowDetail>>(
        `/v1/stop-workflows/${id}`,
        payload
      );
      return response.data.data;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Promote a workflow to its slot's default, demoting whatever held it.
   * Deliberately separate from `update`: that sends the whole definition, so
   * flipping the flag through it would let a stale tab revert someone else's
   * milestone edit. This carries no definition data at all.
   */
  setDefault: async (id: string): Promise<WorkflowDetail> => {
    try {
      const response = await Axios.put<Envelope<WorkflowDetail>>(
        `/v1/stop-workflows/${id}/default`
      );
      return response.data.data;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },
};

export const WORKFLOW_STATUS_LABELS: Record<WorkflowStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

/**
 * THE stop-type / intent label set (CR6-D4) — shared by the workflow master,
 * the Route Planner, the UJP route chips and the 4W stops step so one stop
 * reads the same everywhere.
 */
export const WORKFLOW_STOP_TYPE_LABELS: Record<WorkflowStopType, string> = {
  PICKUP: 'Pickup',
  DROP_OFF: 'Drop off',
};

export const WORKFLOW_EXIM_LABELS: Record<WorkflowExim, string> = {
  EXPORT: 'Export',
  IMPORT: 'Import',
  GENERAL: 'Umum',
};

export const WORKFLOW_STOP_INTENT_LABELS: Record<WorkflowStopIntent, string> = {
  GENERIC: 'Umum',
  UNLOAD: 'Bongkar',
  RETURN_CONTAINER: 'Retur kontainer',
};

export const WORKFLOW_FIELD_TYPE_LABELS: Record<WorkflowFieldType, string> = {
  IMAGE: 'Image',
  TEXT: 'Text',
  NUMBER: 'Number',
  SIGNATURE: 'Signature',
};

/**
 * Field types whose captured value is a file (an array of media URLs) rather
 * than typed text: IMAGE photos, and SIGNATURE — exactly one PNG from the rider
 * pad, or an ops photo of the signed delivery note.
 *
 * The timeline, the driver-evidence section and the ops override (opsEvidence +
 * ForceTerminalModal) all ask "is this a file?" through this one helper, so a
 * new file type is added here once instead of in four places. The workflow
 * builder's IMAGE checks stay IMAGE-only on purpose: they are about IMAGE's
 * photo-count config, which SIGNATURE does not have.
 */
export const FILE_FIELD_TYPES = [
  'IMAGE',
  'SIGNATURE',
] as const satisfies readonly WorkflowFieldType[];
export type FileFieldType = (typeof FILE_FIELD_TYPES)[number];

export const isFileFieldType = (
  type: string | null | undefined
): type is FileFieldType =>
  type != null && (FILE_FIELD_TYPES as readonly string[]).includes(type);
